import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Users,
  Calendar,
  CalendarDays,
  Package,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Building2,
  Megaphone
} from 'lucide-react';
import { Button } from '../ui/button';

const API = RUNTIME_BACKEND_URL;

const navigation = [
  { name: 'Dashboard', href: '/salon', icon: LayoutDashboard },
  { name: 'Müşteriler', href: '/salon/musteriler', icon: Users },
  { name: 'Takvim', href: '/salon/takvim', icon: CalendarDays },
  { name: 'Paketler', href: '/salon/paketler', icon: Package },
  { name: 'Bildirimler', href: '/salon/bildirimler', icon: Bell },
  { name: 'Ayarlar', href: '/salon/ayarlar', icon: Settings },
];

// Tarih formatlama
const formatDateTime = () => {
  const now = new Date();
  const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
  const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  
  const day = days[now.getDay()];
  const date = now.getDate();
  const month = months[now.getMonth()];
  const year = now.getFullYear();
  const hours = now.getHours().toString().padStart(2, '0');
  const minutes = now.getMinutes().toString().padStart(2, '0');
  
  return `${day}, ${date} ${month} ${year} ${hours}:${minutes}`;
};

export default function SalonLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(formatDateTime());
  const [siteSettings, setSiteSettings] = useState({ site_title: 'WeddingTakip', logo_url: null, footer_text: '' });
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState([]);
  const { user, logout, updateTheme } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(formatDateTime());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsRes = await fetch(`${API}/api/site-settings`);
        if (settingsRes.ok) {
          const data = await settingsRes.json();
          setSiteSettings(data);
          if (data.site_title) {
            document.title = data.site_title;
          }
        }
        
        const annRes = await fetch(`${API}/api/announcements/active`);
        if (annRes.ok) {
          const data = await annRes.json();
          setAnnouncements(data);
        }
      } catch (error) {
        console.error('Settings load error:', error);
      }
    };
    loadSettings();
  }, []);

  const dismissAnnouncement = (id) => {
    setDismissedAnnouncements(prev => [...prev, id]);
  };

  const visibleAnnouncements = announcements.filter(a => !dismissedAnnouncements.includes(a.id));

  const handleLogout = () => {
    logout();
    navigate('/giris');
  };

  const toggleTheme = () => {
    const newTheme = user?.theme === 'dark' ? 'light' : 'dark';
    updateTheme(newTheme);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 transform bg-card border-r border-border transition-transform duration-300 ease-in-out lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b border-border">
            <div className="flex items-center gap-2">
              {siteSettings.logo_url ? (
                <img 
                  src={`${API}${siteSettings.logo_url}`} 
                  alt={siteSettings.site_title}
                  className="h-8 w-auto object-contain"
                />
              ) : (
                <Building2 className="h-6 w-6 text-primary" />
              )}
              <span className="text-xl font-semibold gradient-text">{siteSettings.site_title}</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === '/salon'}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )
                }
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border space-y-3">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-medium text-primary">
                  {user?.full_name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.company_name}</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="flex-1"
              >
                {user?.theme === 'dark' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="flex-1 text-destructive hover:text-destructive"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border bg-background/95 backdrop-blur px-4 sm:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-muted-foreground hover:text-foreground"
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="flex-1" />
          
          <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarDays className="h-4 w-4" />
            {currentTime}
          </div>
        </header>

        {/* Announcements */}
        {visibleAnnouncements.length > 0 && (
          <div className="px-4 sm:px-6 py-2 space-y-2">
            {visibleAnnouncements.map((ann) => (
              <div 
                key={ann.id}
                className="bg-primary/10 border border-primary/20 rounded-lg px-4 py-2 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Megaphone className="h-4 w-4 text-primary flex-shrink-0" />
                  <p className="text-sm">
                    <span className="font-medium text-sm">{ann.title}:</span>{' '}
                    <span className="text-muted-foreground">{ann.content}</span>
                  </p>
                </div>
                <button 
                  onClick={() => dismissAnnouncement(ann.id)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>

        {/* Footer */}
        {siteSettings.footer_text && (
          <footer className="border-t border-border bg-card/50 px-4 py-3 text-center">
            <p className="text-sm text-muted-foreground">{siteSettings.footer_text}</p>
          </footer>
        )}
      </div>
    </div>
  );
}
